import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { formResolver } from "../../lib/formResolver";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/FeedbackContext";
import { loginSchema, LoginFormData } from "../../schemas/auth.schema";
import AuthLayout from "../../layouts/AuthLayout";
import {
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiArrowRight,
} from "react-icons/fi";

const getSafeRedirectPath = (roles: string[] = []) => {
  const redirectPath = sessionStorage.getItem("redirect_after_login");
  if (redirectPath?.startsWith("/admin") || redirectPath?.startsWith("/super-admin")) {
    return redirectPath;
  }
  const isSuper = roles.includes("Super_Admin") || roles.includes("Super Admin") || roles.includes("super_admin");
  const isImpersonating = sessionStorage.getItem("is_impersonating") === "true";
  if (isSuper && !isImpersonating) {
    return "/super-admin";
  }
  return "/admin/dashboard";
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, roles } = useAuth();
  const toast = useToast();
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const signupSuccess =
    (location.state as { signupSuccess?: string } | null)?.signupSuccess || "";

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(getSafeRedirectPath(roles), { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, roles]);

  useEffect(() => {
    if (sessionStorage.getItem("session_expired") === "1") {
      sessionStorage.removeItem("session_expired");
      toast.warning("Your session expired. Please sign in again.", "Session expired");
    }
  }, [toast]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: formResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError("");
      setIsSubmitting(true);
      await login(data.email, data.password);
      const redirectPath = sessionStorage.getItem("redirect_after_login");
      sessionStorage.removeItem("redirect_after_login");
      if (redirectPath?.startsWith("/admin") || redirectPath?.startsWith("/super-admin")) {
        navigate(redirectPath);
      }
    } catch (err: any) {
      setError(err.message || "Invalid credentials. Please verify your email and password.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200/80 grid grid-cols-1 lg:grid-cols-12 min-h-[520px]">
        {/* Left Side: Modern Emerald Brand Panel */}
        <div className="lg:col-span-5 bg-gradient-to-br from-[#064e3b] via-[#043e2e] to-[#022c22] p-8 sm:p-10 flex flex-col justify-between relative overflow-hidden text-white">
          {/* Subtle architectural background pattern */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          <div className="relative z-10 space-y-6">
            {/* Brand Logo */}
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#34d399" />
                  <path d="M2 17L12 22L22 17" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 12L12 17L22 12" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div>
                <span className="font-black text-xl tracking-tight block leading-tight text-white">
                  Strux<span className="text-emerald-400">CMS</span>
                </span>
                <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-200/70 block">
                  Enterprise Platform
                </span>
              </div>
            </div>

            {/* Headline */}
            <div className="space-y-3 pt-6">
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                Modern Construction Operations & Commercial Suite
              </h2>
              <p className="text-xs font-normal text-emerald-100/75 leading-relaxed">
                Streamline commercial estimates, project site progress, client billing, and tenant resource controls in a single unified cockpit.
              </p>
            </div>
          </div>

          {/* Clean footer */}
          <div className="relative z-10 pt-6 text-[11px] text-emerald-200/50 font-medium">
            <div>
              © {new Date().getFullYear()} StruxCMS. All rights reserved.
            </div>
            <div className="mt-1">
              New to StruxCMS?{" "}
              <Link to="/pricing" className="font-bold text-emerald-300/80 hover:text-emerald-200 transition-colors">
                See pricing
              </Link>
            </div>
          </div>
        </div>

        {/* Right Side: Clean Form */}
        <div className="lg:col-span-7 p-8 sm:p-12 flex flex-col justify-center bg-white">
          <div className="space-y-6 max-w-md w-full mx-auto">
            {/* Header */}
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Sign in to your account</h1>
              <p className="text-xs font-medium text-slate-500 mt-1">
                Enter your administrative credentials to access your organization portal.
              </p>
            </div>

            {/* Signup success banner */}
            {signupSuccess && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl text-xs font-semibold flex items-start gap-2">
                <span className="h-2 w-2 mt-1 rounded-full bg-emerald-500 shrink-0" />
                <span>{signupSuccess}</span>
              </div>
            )}

            {/* Error banner */}
            {error && (
              <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl text-xs font-semibold flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <FiMail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    {...register("email")}
                    className={`w-full pl-10 pr-4 py-3 bg-slate-50/80 border rounded-xl text-xs font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-normal focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 focus:border-slate-300 transition-all ${
                      errors.email ? "border-rose-400 bg-rose-50/30" : "border-slate-200/90"
                    }`}
                    placeholder="name@company.com"
                    disabled={isSubmitting}
                  />
                </div>
                {errors.email && (
                  <p className="text-[11px] font-bold text-rose-500 mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold uppercase text-slate-500 tracking-wider">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-bold text-[#064e3b] hover:text-[#047857] transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <FiLock className="w-4 h-4" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    {...register("password")}
                    className={`w-full pl-10 pr-11 py-3 bg-slate-50/80 border rounded-xl text-xs font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-normal focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 focus:border-slate-300 transition-all ${
                      errors.password ? "border-rose-400 bg-rose-50/30" : "border-slate-200/90"
                    }`}
                    placeholder="••••••••••••"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-[11px] font-bold text-rose-500 mt-1">{errors.password.message}</p>
                )}
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 bg-[#064e3b] hover:bg-[#047857] text-white font-extrabold text-xs rounded-xl shadow-md shadow-emerald-950/20 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-70 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Authenticating...
                    </>
                  ) : (
                    <>
                      Sign in to Dashboard <FiArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;
